import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";

// Divider spine configuration
const DIVIDER_CONFIG = {
  // Scaling multipliers
  widthMultiplier: 4.0,
  heightMultiplier: 1.05,
  // Position offset (positive = right, negative = left)
  xOffsetMultiplier: -0.001,
};

/**
 * DividerController - Spine animation controller for reel dividers
 * Always visible spine animation controller
 */
class DividerController {
  constructor(dividerContainer, dividerDimensions) {
    this.dividerContainer = dividerContainer;
    this.dividerSpineData = {
      skeleton: "divider-skeleton",
      atlas: "divider-atlas",
    };
    this.dividerDimensions = dividerDimensions;

    this.dividerSpine = null;
    this.initialized = false;

    // Import config values instead of hardcoded multipliers
    const cfg = DIVIDER_CONFIG;
    this.widthMultiplier = cfg.widthMultiplier || 1.0;
    this.heightMultiplier = cfg.heightMultiplier || 1.0;
    this.xOffsetMultiplier = cfg.xOffsetMultiplier;

    this.initializeDividerSpine();
  }

  /**
   * Resize divider spine
   */
  resizeDivider(newDividerDimensions) {
    // Early exit if no change
    if (
      newDividerDimensions.width === this.dividerDimensions.width &&
      newDividerDimensions.height === this.dividerDimensions.height
    ) {
      return;
    }

    this.dividerDimensions = newDividerDimensions;

    if (this.dividerSpine && !this.dividerSpine.destroyed) {
      this.applyDividerScaling();
    }
  }

  /**
   * Apply scaling and positioning
   */
  applyDividerScaling() {
    if (!this.dividerSpine || this.dividerSpine.destroyed) {
      return;
    }

    // Get actual spine bounds dynamically (like ReelBackController does)
    const spineBounds = this.dividerSpine.getLocalBounds();
    if (!spineBounds || spineBounds.width <= 0 || spineBounds.height <= 0) {
      setTimeout(() => {
        this.applyDividerScaling();
      }, 100);
      return;
    }

    // Calculate scaling based on target dimensions and actual spine bounds
    const cfg = DIVIDER_CONFIG;
    const requiredSpineWidth = this.dividerDimensions.width;
    const requiredSpineHeight = this.dividerDimensions.height;

    // Calculate scaling
    const scaleX = requiredSpineWidth / spineBounds.width;
    const scaleY = requiredSpineHeight / spineBounds.height;

    // Apply scaling with multipliers
    if (this.dividerSpine.scale) {
      this.dividerSpine.scale.x = scaleX * (cfg.widthMultiplier || 1);
      this.dividerSpine.scale.y = scaleY * (cfg.heightMultiplier || 1);
    }

    // Position the divider in the container with slight right offset
    if (this.dividerSpine.position) {
      const containerWidth = this.dividerDimensions.containerWidth || 0;
      const xOffset = this.dividerDimensions.width * this.xOffsetMultiplier;
      this.dividerSpine.position.x = containerWidth / 2 + xOffset;
      this.dividerSpine.position.y = this.dividerDimensions.height / 2;
    }
  }

  /**
   * Create divider spine
   */
  createDividerSpine() {
    try {
      if (!this.dividerSpineData) {
        return null;
      }

      // Check if assets are loaded
      const assets = PIXI.Assets.cache;
      if (
        !assets.has(this.dividerSpineData.skeleton) ||
        !assets.has(this.dividerSpineData.atlas)
      ) {
        return null;
      }

      // Create spine instance
      const dividerSpine = Spine.from({
        skeleton: this.dividerSpineData.skeleton,
        atlas: this.dividerSpineData.atlas,
      });

      // Verify the spine was created properly
      if (!dividerSpine || !dividerSpine.skeleton || !dividerSpine.state) {
        return null;
      }

      // Verify skeleton has required data
      if (
        !dividerSpine.skeleton.data ||
        !dividerSpine.skeleton.data.animations
      ) {
        return null;
      }

      return dividerSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get available animation names
   */
  getAvailableAnimations() {
    if (!this.dividerSpine?.skeleton?.data?.animations) {
      return [];
    }
    return this.dividerSpine.skeleton.data.animations.map((anim) => anim.name);
  }

  /**
   * Initialize divider spine and start animation automatically (like ReelBackController)
   */
  initializeDividerSpine() {
    this.dividerSpine = this.createDividerSpine();

    if (!this.dividerSpine) {
      return;
    }

    // Setup spine properties
    this.dividerSpine.position.set(0, 0);
    this.dividerSpine.visible = true;

    // Add to container
    this.dividerContainer.addChild(this.dividerSpine);

    // Apply scaling
    this.applyDividerScaling();

    // Start the divider animation automatically (like ReelBackController)
    if (
      this.dividerSpine &&
      !this.dividerSpine.destroyed &&
      this.dividerSpine.state
    ) {
      // Reset spine state
      this.dividerSpine.state.clearTracks();
      this.dividerSpine.state.clearListeners();
      this.dividerSpine.state.apply(this.dividerSpine.skeleton);
      // Use setBonesToSetupPose() instead of setToSetupPose() — resets bones without slot attachment changes (no DarkSlotMesh leak)
      this.dividerSpine.skeleton.setBonesToSetupPose();

      // Play the divider animation (looping)
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.dividerSpine.state.setAnimation(0, animations[0], true);
      }
    }

    this.initialized = true;
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    if (this.dividerSpine && !this.dividerSpine.destroyed) {
      // Clear animation state
      if (this.dividerSpine.state) {
        this.dividerSpine.state.clearTracks();
        this.dividerSpine.state.clearListeners();
      }

      // Remove from container
      if (this.dividerSpine.parent) {
        this.dividerSpine.parent.removeChild(this.dividerSpine);
      }

      // Destroy spine instance
      this.dividerSpine.destroy({
        children: true,
        texture: false,
        baseTexture: false,
      });
    }

    // Clear references
    this.dividerSpine = null;
    this.dividerContainer = null;
    this.dividerSpineData = null;
    this.initialized = false;
  }
}

export default DividerController;
